import {
  ConfirmDialogMui as ConfirmDialog,
  useAlert,
  useDialog,
  useNavigate,
} from '@spotify/confidence-core-react';
import { useDeleteWorkflowInstanceMutation } from '@spotify/confidence-graphql';

import { useFormatWorkflowDisplayName } from '../../hooks/useFormatWorkflowDisplayName';
import { WorkflowInstance } from '../../hooks/useGetWorkflowInstance';

export type DeleteWorkflowInstanceDialogProps = {
  instance: WorkflowInstance;
  workflowId: string;
};

export const DeleteWorkflowInstanceDialog = ({
  instance,
  workflowId,
}: DeleteWorkflowInstanceDialogProps) => {
  const alert = useAlert();
  const { closeDialog } = useDialog();
  const navigate = useNavigate();
  const formatWorkflowDisplayName = useFormatWorkflowDisplayName();

  const [deleteWorkflowInstance] = useDeleteWorkflowInstanceMutation({
    update: cache => {
      cache.evict({
        id: cache.identify({
          __typename: 'WorkflowV1WorkflowInstance',
          name: instance.name,
        }),
      });
    },
    onCompleted: response => {
      if (
        !!response.deleteWorkflowInstance &&
        'message' in response.deleteWorkflowInstance
      ) {
        alert.post({
          severity: 'error',
          message:
            response.deleteWorkflowInstance.message ?? 'Something went wrong',
        });
      } else {
        alert.post({
          severity: 'success',
          message: `${instance?.displayName} was successfully deleted`,
          display: 'transient',
        });

        // Todo: Remove timeout when there is proper backend support
        setTimeout(() => {
          closeDialog();
          navigate(`workflows/${workflowId}`);
        }, 1000);
      }
    },
  });

  const onDelete = async () => {
    await deleteWorkflowInstance({
      variables: {
        name: instance.name,
      },
    });
  };

  const workflowLabel = formatWorkflowDisplayName(workflowId);
  const capitalizedWorkflowLabel = formatWorkflowDisplayName(workflowId, {
    capitalize: true,
  });

  return (
    <ConfirmDialog
      expected="delete"
      title={`Delete ${capitalizedWorkflowLabel}`}
      irreversible
      onConfirm={onDelete}
     data-track-source-anchor="ef8098ed54;delete-workflow-instance-dialog-confirm-dialog">
      This will delete the {workflowLabel}. The action can not be undone. Do you
      wish to proceed?
    </ConfirmDialog>
  );
};
